import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import {twMerge} from '../../../common/twMerge';
import {ZIndex} from '../../../domain/content/[seriesId]/viewer/[productId]/type/ZIndex.enum';
import {TalkToastProps} from './TalkToastProps.interface';
import {TalkToastElement} from './ToastElement.interface';

export const TalkToast = forwardRef<TalkToastElement, TalkToastProps>(
  (
    {transitionTime = 0.5, exposingTime = 1.5, children, className, ...props},
    ref
  ) => {
    const opacityTimerRef = useRef(0);
    const visibilityTimerRef = useRef(0);
    const divRef = useRef<HTMLDivElement>(null);
    // 애니메이션 지속시간 (뜰때, 내려갈때) 합이 노출시간보다 길면 중간에 갑자기 사라져버리므로 노출시간의 절반을 최대값으로 둔다.
    const _transitionTime = Math.min(transitionTime, exposingTime / 2);

    const concealToast = useCallback(() => {
      window.clearTimeout(opacityTimerRef.current);
      window.clearTimeout(visibilityTimerRef.current);
      if (divRef.current) {
        divRef.current.style.opacity = '0';
        visibilityTimerRef.current = window.setTimeout(() => {
          if (divRef.current) {
            divRef.current.style.visibility = 'hidden';
          }
        }, _transitionTime * 1000);
      }
    }, [_transitionTime]);

    const exposeToast = useCallback(() => {
      window.clearTimeout(opacityTimerRef.current);
      window.clearTimeout(visibilityTimerRef.current);
      if (exposingTime < 0) {
        concealToast();
        return;
      }
      if (divRef.current) {
        divRef.current.style.visibility = 'visible';
        divRef.current.style.opacity = '1';
        opacityTimerRef.current = window.setTimeout(
          concealToast,
          (exposingTime - _transitionTime) * 1000
        );
      }
    }, [concealToast, exposingTime, _transitionTime]);

    useEffect(() => {
      return () => {
        window.clearTimeout(opacityTimerRef.current);
        window.clearTimeout(visibilityTimerRef.current);
      };
    }, []);

    useImperativeHandle(ref, () => ({
      expose: exposeToast,
      conceal: concealToast,
      innerElement: divRef.current,
    }));

    return (
      <div
        className={twMerge('invisible opacity-0', className)}
        style={{
          transition: `opacity ${_transitionTime}s linear`,
          zIndex: ZIndex.ViewerMenu - 1,
        }}
        ref={divRef}
        {...props}
      >
        {children}
      </div>
    );
  }
);
